import { apiClient } from "../services/apiClient";
import { ATP_API_URL } from "../config/apiConfig";

const s = (v) => (v !== null && v !== undefined ? String(v) : "");
const b = (v) => v === true || v === "yes" || v === "true";
const hasPath = (v) => !!(v && v !== "string");

/**
 * Updates entity primary + KYC data.
 * POST api/Onboarding/update-entity
 */
export const updateEntityAction = async (_entityId, entityData) => {
  try {
    const now = new Date().toISOString();
    const e = entityData;

    // Build gstList — preserve isGST/isGSTDef from API; derive isGST from path if not set
    const gstList = Array.isArray(e.gstList)
      ? e.gstList.map((g, i) => ({
          id: g.gstId || g.id || i + 1,
          gstNumber: s(g.gstNumber),
          state: s(g.state || g.gstState),
          gstAddress: s(g.gstAddress),
          gstCertificatePath: s(g.gstCertificatePath),
          isGST: false,
          isGSTDef: g.isGSTDef === true,
        }))
      : [];

    // Build uploadedList — collect every document path that exists on the entity
    // Each entry: { success, message, id, fileName, filePath, documentType, extension }
    const makeUploadEntry = (filePath, documentType) => {
      if (!hasPath(filePath)) return null;
      const cleanPath = String(filePath);
      const fileName = cleanPath.split(/[\\/]/).pop() || documentType;
      const extension = fileName.includes(".")
        ? "." + fileName.split(".").pop()
        : "";
      return {
        success: true,
        message: "",
        id: 0,
        fileName,
        filePath: cleanPath,
        documentType,
        extension,
      };
    };

    const docPaths = [
      // Aadhar / PAN
      makeUploadEntry(e.aadharCardPath, "AadharCardPath"),
      makeUploadEntry(e.aadharCardBackPath, "AadharCardBackPath"),
      makeUploadEntry(e.panCardPath, "PANCardPath"),
      // Partnership / LLP
      makeUploadEntry(e.partnershipDeedPath, "PartnershipDeedPath"),
      makeUploadEntry(e.aadharOfBothPartnersPath, "AadharOfBothPartnersPath"),
      makeUploadEntry(
        e.aadharOfBothPartnersPathSecond,
        "AadharOfBothPartnersPathSecond",
      ),
      makeUploadEntry(e.uploadLLPCertificatePath, "UploadLLPCertificatePath"),
      // Private / Public Limited
      makeUploadEntry(
        e.certificateOfIncorporationPath,
        "CertificateOfIncorporationPath",
      ),
      makeUploadEntry(e.directorPANPath, "DirectorPANPath"),
      makeUploadEntry(e.directorAadharPath, "DirectorAadharPath"),
      // Trust
      makeUploadEntry(e.trustDeedPath, "TrustDeedPath"),
      makeUploadEntry(e.panOfTrustPath, "PANOfTrustPath"),
      makeUploadEntry(e.panOfTrusteePath, "PANOfTrusteePath"),
      makeUploadEntry(e.aadharOfTrusteePath, "AadharOfTrusteePath"),
      // MSME / Utility / Bank
      makeUploadEntry(e.msmeCertificatePath, "MSMECertificatePath"),
      makeUploadEntry(e.utilityBillDocumentPath, "UtilityBillDocumentPath"),
      makeUploadEntry(
        e.passbookOrCancelledChequePath,
        "PassbookOrCancelledChequePath",
      ),
      makeUploadEntry(e.uploadSignedFormPath, "UploadSignedFormPath"),
    ].filter(Boolean);

    // GST certificates
    if (Array.isArray(e.gstList)) {
      e.gstList.forEach((g) => {
        const entry = makeUploadEntry(
          g.gstCertificatePath,
          "GSTCertificatePath",
        );
        if (entry) docPaths.push(entry);
      });
    }

    // Merge: any pre-existing uploadedList entries + newly built ones (deduplicate by filePath)
    const existingUploaded = Array.isArray(e.uploadedList)
      ? e.uploadedList
      : [];
    const existingPaths = new Set(existingUploaded.map((u) => u.filePath));
    const newEntries = docPaths.filter((d) => !existingPaths.has(d.filePath));
    const uploadedList = [...existingUploaded, ...newEntries];

    const payload = {
      // ── Identifiers ──────────────────────────────────────────────────────
      entityPrimaryId: e.entityPrimaryId || e.id || 0,
      kycId: parseInt(e.kycId, 10) || 0, // number — 0 if not set

      // ── KYC top-level fields ─────────────────────────────────────────────
      tradeName: s(e.tradeName),
      pinCode: s(e.pinCode),
      city: s(e.city),
      state: s(e.state || e.State),
      address: s(e.address),
      natureOfBusiness: s(e.natureOfBusiness),
      invoiceRequired: b(e.invoiceRequired ?? e.isInvoiceRequired),
      entityType: s(e.entityType),

      // ── Contact person ────────────────────────────────────────────────────
      contactPersonDetails: {
        contactPersonName: s(e.contactPersonName),
        designation: s(e.contactPersonDesignation || e.designation),
        mobileNumber: s(e.contactPersonMobile || e.mobileNumber),
        contactPersonEmail: s(e.contactEmail || e.contactPersonEmail),
      },

      // ── Aadhar details ────────────────────────────────────────────────────
      adharDetails: {
        aadharCardNumber: s(e.aadharCardNumber),
        aadharCardPath: s(e.aadharCardPath),
        aadharCardUploaded:
          e.aadharCardUploaded === true || hasPath(e.aadharCardPath),
        aadharCardBackPath: s(e.aadharCardBackPath),
        aadharCardBackUploaded:
          e.aadharCardBackUploaded === true || hasPath(e.aadharCardBackPath),
        isAadharCard: false,
        isAadharCardDef: e.isAadharCardDef === true,
      },

      // ── PAN details ───────────────────────────────────────────────────────
      panDetails: {
        panCardNumber: s(e.panCardNumber || e.CompanypanNumber),
        panCardPath: s(e.panCardPath),
        panCardUploaded: e.panCardUploaded === true || hasPath(e.panCardPath),
        isPANCard: false,
        isPANCardDef: e.isPANCardDef === true,
      },

      // ── Partnership ───────────────────────────────────────────────────────
      partnershipDetails: {
        partnershipDeedPath: s(e.partnershipDeedPath),
        partnershipDeedUploaded:
          e.partnershipDeedUploaded === true || hasPath(e.partnershipDeedPath),
        isPartnershipDeed: false,
        isPartnershipDeedDef: e.isPartnershipDeedDef === true,
        aadharNumberOfBothPartners: s(e.aadharNumberOfBothPartners),
        aadharOfBothPartnersPath: s(e.aadharOfBothPartnersPath),
        aadharOfBothPartnersUploaded:
          e.aadharOfBothPartnersUploaded === true ||
          hasPath(e.aadharOfBothPartnersPath),
        isAadharOfBothPartners: false,
        isAadharOfBothPartnersDef: e.isAadharOfBothPartnersDef === true,
        aadharNumberOfBothPartnersSecond: s(e.aadharNumberOfBothPartnersSecond),
        aadharOfBothPartnersPathSecond: s(e.aadharOfBothPartnersPathSecond),
        aadharOfBothPartnersUploadedSecond:
          e.aadharOfBothPartnersUploadedSecond === true ||
          hasPath(e.aadharOfBothPartnersPathSecond),
        isAadharOfBothPartnersSecond: false,
        isAadharOfBothPartnersDefSecond:
          e.isAadharOfBothPartnersDefSecond === true,
      },

      // ── LLP ───────────────────────────────────────────────────────────────
      llpDetails: {
        llpCrtificateNumber: s(e.llpCrtificateNumber),
        uploadLLPCertificatePath: s(e.uploadLLPCertificatePath),
        llpCertificateUploaded:
          e.llpCertificateUploaded === true ||
          hasPath(e.uploadLLPCertificatePath),
        isLLPCertificate: false,
        isLLPCertificateDef: e.isLLPCertificateDef === true,
        partnershipDeedPath: s(e.partnershipDeedPath),
        partnershipDeedUploaded:
          e.partnershipDeedUploaded === true || hasPath(e.partnershipDeedPath),
        isPartnershipDeed: false,
        isPartnershipDeedDef: e.isPartnershipDeedDef === true,
        aadharNumberOfBothPartners: s(e.aadharNumberOfBothPartners),
        aadharOfBothPartnersPath: s(e.aadharOfBothPartnersPath),
        aadharOfBothPartnersUploaded:
          e.aadharOfBothPartnersUploaded === true ||
          hasPath(e.aadharOfBothPartnersPath),
        isAadharOfBothPartners: false,
        isAadharOfBothPartnersDef: e.isAadharOfBothPartnersDef === true,
        aadharNumberOfBothPartnersSecond: s(e.aadharNumberOfBothPartnersSecond),
        aadharOfBothPartnersPathSecond: s(e.aadharOfBothPartnersPathSecond),
        aadharOfBothPartnersUploadedSecond:
          e.aadharOfBothPartnersUploadedSecond === true ||
          hasPath(e.aadharOfBothPartnersPathSecond),
        isAadharOfBothPartnersSecond: false,
        isAadharOfBothPartnersDefSecond:
          e.isAadharOfBothPartnersDefSecond === true,
      },

      // ── Private / Public Limited ──────────────────────────────────────────
      privatePublicLimitedCompanyDetails: {
        certificateOfIncorporationNumber: s(e.certificateOfIncorporationNumber),
        certificateOfIncorporationPath: s(e.certificateOfIncorporationPath),
        certificateOfIncorporationUploaded:
          e.certificateOfIncorporationUploaded === true ||
          hasPath(e.certificateOfIncorporationPath),
        isCertificateOfIncorporation: false,
        isCertificateOfIncorporationDef:
          e.isCertificateOfIncorporationDef === true,
        directorPANNumber: s(e.directorPANNumber),
        directorPANPath: s(e.directorPANPath),
        directorPanUploaded:
          e.directorPanUploaded === true || hasPath(e.directorPANPath),
        isDirectorPan: false,
        isDirectorPanDef: e.isDirectorPanDef === true,
        directorAadharNumber: s(e.directorAadharNumber),
        directorAadharPath: s(e.directorAadharPath),
        directorAadharUploaded:
          e.directorAadharUploaded === true || hasPath(e.directorAadharPath),
        isDirectorAadhar: false,
        isDirectorAadharDef: e.isDirectorAadharDef === true,
      },

      // ── Trust ─────────────────────────────────────────────────────────────
      trustDetails: {
        trustDeedNumber: s(e.trustDeedNumber),
        trustDeedPath: s(e.trustDeedPath),
        trustDeedUploaded:
          e.trustDeedUploaded === true || hasPath(e.trustDeedPath),
        isTrustDeed: false,
        isTrustDeedDef: e.isTrustDeedDef === true,
        trustPanNumber: s(e.panOfTrustNumber),
        trustPanPath: s(e.panOfTrustPath),
        trustPanUploaded:
          e.trustPanUploaded === true || hasPath(e.panOfTrustPath),
        isPANOfTrust: false,
        isPANOfTrustDef: e.isPANOfTrustDef === true,
        trusteePanNumber: s(e.panOfTrusteeNumber),
        trusteePanPath: s(e.panOfTrusteePath),
        trusteePanUploaded:
          e.trusteePanUploaded === true || hasPath(e.panOfTrusteePath),
        isPANOfTrustee: false,
        isPANOfTrusteeDef: e.isPANOfTrusteeDef === true,
        trusteeAadharNumber: s(e.aadharNumberOfTrustee),
        trusteeAadharPath: s(e.aadharOfTrusteePath),
        trusteeAadharUploaded:
          e.trusteeAadharUploaded === true || hasPath(e.aadharOfTrusteePath),
        isAadharOfTrustee: false,
        isAadharOfTrusteeDef: e.isAadharOfTrusteeDef === true,
      },

      // ── MSME ──────────────────────────────────────────────────────────────
      msmeDetails: {
        isRegisteredUnderMSME: b(e.isMSMERegistered ?? e.isRegisteredUnderMSME),
        msmeRegistrationNumber: s(e.msmeNumber || e.msmeRegistrationNumber),
        msmeStatus: s(e.msmeStatus),
        msmeCertificatePath: s(e.msmeCertificatePath),
        msmeCertificateUploaded:
          e.msmeCertificateUploaded === true || hasPath(e.msmeCertificatePath),
        isMSMECertificate: false,
        isMSMECertificateDef: e.isMSMECertificateDef === true,
      },

      // ── Utility bill ──────────────────────────────────────────────────────
      utilityBillDetails: {
        utilityType: s(e.utilityType),
        providerName: s(e.utilityProviderName || e.providerName),
        accountOrConsumerNumber: s(
          e.utilityAccountNumber || e.accountOrConsumerNumber,
        ),
        addressOnUtilityBill: s(e.utilityAddress || e.addressOnUtilityBill),
        utilityBillDocumentPath: s(e.utilityBillDocumentPath),
        utilityBillDocumentUploaded:
          e.utilityBillDocumentUploaded === true ||
          hasPath(e.utilityBillDocumentPath),
        isUtilityBillDocument: false,
        isUtilityBillDocumentDef: e.isUtilityBillDocumentDef === true,
      },

      // ── Bank details ──────────────────────────────────────────────────────
      bankDetails: {
        provideBankDetails: b(e.hasBankDetails ?? e.provideBankDetails),
        bankName: s(e.bankName),
        accountNumber: s(e.accountNumber),
        ifscCode: s(e.ifscCode),
        accountType: s(e.accountType),
        branchName: s(e.branchName),
        beneficiaryName: s(e.beneficiaryName),
        relationWithBeneficiary: s(e.relationWithBeneficiary),
        passbookOrCancelledChequePath: s(e.passbookOrCancelledChequePath),
        passbookOrCancelledChequeUploaded:
          e.passbookOrCancelledChequeUploaded === true ||
          hasPath(e.passbookOrCancelledChequePath),
        isPassbookOrCancelledCheque: false,
        isPassbookOrCancelledChequeDef:
          e.isPassbookOrCancelledChequeDef === true,
        uploadSignedFormPath: s(e.uploadSignedFormPath),
        signedFormUploaded:
          e.signedFormUploaded === true || hasPath(e.uploadSignedFormPath),
        isSignedForm: false,
        isSignedFormDef: e.isSignedFormDef === true,
      },

      // ── GST ───────────────────────────────────────────────────────────────
      isGSTRegistered: b(e.isGSTRegistered),
      gstList: gstList, // always send, empty array when not registered

      // ── Uploaded file list ────────────────────────────────────────────────
      uploadedList,

      // ── Timestamps ───────────────────────────────────────────────────────
      createdBy: s(e.createdBy),
      createdOn: s(e.createdOn) || now,
      modifiedBy: s(e.modifiedBy) || "Admin",
      modifiedOn: now,
      isDocumentUploaded: e.isDocumentUploaded === true,
      isSaveAsDraft: e.isSaveAsDraft === true,

      // ── Primary / entity-level fields ────────────────────────────────────
      parentCode: s(e.parentCode || e.parentId),
      parentName: s(e.parentName),
      entityName: s(e.entityName),
      entityCategory: s(e.entityCategory),
      entityLOBType: s(e.entityLOBType || e.lobType),
      aaDepartmentFunction: s(e.aaDepartmentFunction),
      emailAddress: s(e.emailAddress || e.contactPersonEmail || e.contactEmail),
      spoC_EmailAddress: s(e.spocEmailAddress || e.spoC_EmailAddress),
      kyC_URL: s(e.kyC_URL),
      requireKYC: b(e.requireKYC),
    };

    // console.log("[UpdateEntity] payload:", {
    //   entityPrimaryId: payload.entityPrimaryId,
    //   kycId: payload.kycId,
    //   entityType: payload.entityType,
    //   entityName: payload.entityName,
    //   uploadedListCount: payload.uploadedList?.length,
    //   uploadedList: payload.uploadedList,
    // });

    const response = await apiClient.post(
      ATP_API_URL.UPDATE_FORM_ALL_DATA,
      payload,
    );

    if (response?.status === 200) {
      if (response.data?.success === false) {
        return {
          success: false,
          message:
            response.data.message ||
            "Update failed. Please check the data and try again.",
        };
      }
      return {
        success: true,
        data: response.data,
        message: response.data?.message || "Entity updated successfully",
      };
    }

    return {
      success: false,
      message: response?.data?.message || "Failed to update entity",
    };
  } catch (error) {
    console.error("[UpdateEntity] error:", error);
    return {
      success: false,
      message: error.message || "Failed to update entity",
      error,
    };
  }
};
